import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Activity, TrendingDown } from "lucide-react";
import { useRiskMetric } from "@/hooks/useRiskMetrics";

interface RiskProfileCardProps {
  symbol: string;
}

function fmt(n: number | null | undefined, suffix = "", digits = 2) {
  if (n == null || !Number.isFinite(n)) return "—";
  return `${n.toFixed(digits)}${suffix}`;
}

function verdict(sharpe: number | null, dd: number | null): string {
  if (sharpe == null) return "Not enough price history yet to compute a reliable risk profile.";
  const ddTxt = dd != null ? ` Worst pullback: ${dd.toFixed(0)}%.` : "";
  if (sharpe >= 1.5) return `Excellent risk-adjusted returns — A$1 of risk earned A$${sharpe.toFixed(2)} over 90 days.${ddTxt}`;
  if (sharpe >= 1) return `Strong risk-adjusted returns — A$1 of risk earned A$${sharpe.toFixed(2)} over 90 days.${ddTxt}`;
  if (sharpe >= 0.3) return `Average risk-adjusted returns — return barely compensated for the volatility.${ddTxt}`;
  if (sharpe >= 0) return `Weak risk-adjusted returns — barely beat the cash rate after volatility.${ddTxt}`;
  return `Negative risk-adjusted returns — holders were paid less than cash for taking the risk.${ddTxt}`;
}

export function RiskProfileCard({ symbol }: RiskProfileCardProps) {
  const { metric, isLoading } = useRiskMetric(symbol);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-primary" />
          <CardTitle className="text-base">Risk Profile (AUD)</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && <div className="text-sm text-muted-foreground">Loading risk metrics…</div>}
        {!isLoading && !metric && (
          <div className="text-sm text-muted-foreground">
            No risk metrics yet for {symbol.toUpperCase()}. Metrics are calculated nightly from AUD price snapshots.
          </div>
        )}
        {metric && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Sharpe 30d" value={fmt(metric.sharpe_30d)} />
              <Stat label="Sharpe 90d" value={fmt(metric.sharpe_90d)} highlight />
              <Stat label="Sharpe 180d" value={fmt(metric.sharpe_180d)} />
              <Stat label="Sortino 90d" value={fmt(metric.sortino_90d)} />
              <Stat label="Ann. Volatility" value={fmt(metric.volatility_ann_90d, "%", 1)} />
              <Stat label="Max DD 90d" value={fmt(metric.max_drawdown_90d, "%", 1)} negative />
              <Stat label="Max DD 180d" value={fmt(metric.max_drawdown_180d, "%", 1)} negative />
              <Stat label="Current DD" value={fmt(metric.current_drawdown_pct, "%", 1)} negative />
            </div>

            {(metric.max_dd_peak_date || metric.max_dd_recovery_days != null) && (
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <TrendingDown className="w-3 h-3" />
                {metric.max_dd_peak_date && metric.max_dd_trough_date && (
                  <span>
                    Worst run: {metric.max_dd_peak_date} → {metric.max_dd_trough_date}
                    {metric.max_dd_recovery_days != null && ` • recovered in ${metric.max_dd_recovery_days}d`}
                    {metric.max_dd_recovery_days == null && " • not yet recovered"}
                  </span>
                )}
              </div>
            )}

            <div className="text-sm text-foreground/90 border-l-2 border-primary pl-3">
              {verdict(metric.sharpe_90d, metric.max_drawdown_90d)}
            </div>

            <div className="text-xs text-muted-foreground">
              Based on {metric.data_points_used ?? "—"} daily AUD price points • Updated{" "}
              {new Date(metric.calculated_at).toLocaleString("en-AU")}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, highlight, negative }: { label: string; value: string; highlight?: boolean; negative?: boolean }) {
  const valueClass = highlight
    ? "text-primary"
    : negative && value !== "—" && value.startsWith("-")
    ? "text-destructive"
    : "text-foreground";
  return (
    <div className="space-y-1">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-base font-mono font-semibold ${valueClass}`}>{value}</div>
    </div>
  );
}
